// Console publisher filter: the ADMIN/MODERATOR-only FilterNews list behind
// the "Console" burger-menu entry. A row's `name` is the publisher string
// exactly as Google News writes it after "- " at the end of a headline, and
// the backend match is a case-sensitive String.endsWith - so nothing here may
// normalise, lowercase or dedupe a name.
//
// Backend is live at REACT_APP_ONELIKE_BACKEND_API
// (https://api.onelike.app/service). The per-language vars (/dienst,
// /servicefr, ...) are different controllers WITHOUT these endpoints, and the
// filter list is global across all 15 languages anyway, so only the
// unsuffixed base is ever used.
//
// Modelled on usercontainer.service.js for { ok, status, body } only.
// Deliberately NOT inherited from it:
//
// 1. No toIdentity / UserContainerClass / replacer. No request body here
//    carries uuid, email or any other identity field: identity travels only
//    in the Authorization: Bearer header, because the role must not be
//    client-assertable.
// 2. No AbortController and no timeout. These are plain database reads and
//    writes; the 60s cut-off there exists because POST /container does a live
//    upstream Google News fetch server-side.
// 3. No `language` field and no per-language base URL (see above).
// 4. Its bare `.then(toResult, () => networkResult())` is not enough here:
//    see settled() below for why this file adds a terminal .catch on top.
//
// Also not modelled on newspin.service.js (bare res.json(), so the HTTP
// status is unrecoverable and every error branch of this API needs it) or on
// outofscope.service.js (that is the right precedent for the role GATING, but
// it posts to workerConstants.WORKER, a different backend).
//
// All mutations are POST, including the hard delete; this API has no PUT and
// no DELETE.

import { authHeaderWithJSON } from "../helpers";

const API = process.env.REACT_APP_ONELIKE_BACKEND_API;

// Reads the body as text and tries JSON.parse, so an empty or non-JSON error
// body never throws (same defensive shape as usercontainer.service.js).
function toResult(res) {
    return res.text().then(text => {
        let body;
        try {
            body = text ? JSON.parse(text) : undefined;
        } catch (e) {
            body = undefined;
        }
        return { ok: res.ok, status: res.status, body: body };
    });
}

// A rejected fetch (network/DNS/CORS) is reported as status 0, not thrown, so
// callers only ever have one shape to handle.
function networkResult() {
    return { ok: false, status: 0, body: undefined };
}

// Terminal guard, DELIBERATELY STRICTER than the precedent - do not
// "simplify" it back to a bare .then(toResult, () => networkResult()) to
// match usercontainer.service.js.
//
// That two-arm shape catches a rejected fetch (network/DNS/CORS) but NOT a
// throw inside toResult itself: res.text() rejects on its own when the
// connection drops mid-body, and the rejection then passes straight through
// to the caller. No call site in ConsolePopup.js attaches a .catch(), so
// such a rejection would skip setLoading(false) / setAdding(false) /
// setBusyId(null) entirely, leaving the popup's `busy` flag true forever -
// and both of its exits (Escape/backdrop via onClose, and the Close button)
// are gated on `busy`, so the dialog would be unclosable short of a page
// reload. usercontainer.service.js carries the equivalent guard on all of
// its calls for exactly this reason - create() first, then post() and
// getMyContainers() once the same trap was found to be live in
// CardConfigPopup, where the stuck flag is in Redux and so survives
// closing and reopening the dialog.
//
// So every call here resolves and none rejects: a body that cannot be read
// is reported exactly like a failed fetch - { ok: false, status: 0, body:
// undefined } - which is the outcome-unknown marker the status === 0
// branches of handleFailure already handle (re-read the list for a write,
// "can't reach OneLike" for the list itself).
function settled(promise) {
    return promise
        .then(toResult, () => networkResult())
        .catch(() => networkResult());
}

function post(path, payload) {
    return settled(fetch(`${API}${path}`,
        {
            method: 'POST',
            headers: authHeaderWithJSON(),
            body: JSON.stringify(payload)
        }));
}

// -> { filters: [ { id, name, active, createdby, createdat } ] }, every row
// both active and inactive, already sorted id-descending server-side: do not
// re-sort. `active` is never null on the wire; `createdby`/`createdat` ARE
// null on rows predating this feature, and `createdat` is a Java
// Date.toString() string ("Sat Oct 11 17:06:51 CEST 2026"), not ISO-8601.
//
// No params and no body, yet authHeaderWithJSON() all the same: the token is
// the only identity this endpoint has. Three live GETs already do exactly
// this (/mycontainers, /mypinnednews, /pincount) - the helper returns a flat
// header object with no reference to the method or the body.
function getFilters() {
    return settled(fetch(`${API}/filternews`,
        {
            method: 'GET',
            headers: authHeaderWithJSON()
        }));
}

// -> { added, reactivated, filter }. Idempotent, with three success shapes:
// added true = new row; reactivated true = the row existed inactive and is
// now on; both false = it was already active. None of the three is an error.
// The name is trimmed and re-validated server-side (empty / >120 / control
// characters -> 400 filternews.name.invalid).
function addFilter(name) {
    return post('/filternews', { name: name });
}

// -> { updated: true, filter }. The reversible half of the pair; a legacy
// row that arrives active:false because its column is NULL is repaired
// permanently by one call here.
function setActive(id, active) {
    return post('/filternews/active', { id: id, active: active });
}

// -> { deleted: true }. A hard delete - the `active` toggle is the soft
// option and both exist deliberately. POST, not DELETE.
function deleteFilter(id) {
    return post('/filternews/delete', { id: id });
}

export const filternewsService = {
    getFilters,
    addFilter,
    setActive,
    deleteFilter
};
